import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getAvailableModelsWithApiKeys } from '../services/api';

const ApiKeysContext = createContext();

export const useApiKeys = () => {
  const context = useContext(ApiKeysContext);
  if (!context) {
    throw new Error('useApiKeys must be used within an ApiKeysProvider');
  }
  return context;
};

export const ApiKeysProvider = ({ children }) => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    anthropic: ''
  });
  const [apiKeyStatus, setApiKeyStatus] = useState({
    openai: { valid: false, available: false },
    anthropic: { valid: false, available: false }
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load API keys from user metadata
  useEffect(() => {
    if (user && user.user_metadata?.api_keys) {
      const userApiKeys = user.user_metadata.api_keys;
      setApiKeys({
        openai: userApiKeys.openai || '',
        anthropic: userApiKeys.anthropic || ''
      });
      setApiKeyStatus({
        openai: { 
          valid: userApiKeys.openai_valid || false, 
          available: !!userApiKeys.openai 
        },
        anthropic: { 
          valid: userApiKeys.anthropic_valid || false, 
          available: !!userApiKeys.anthropic 
        }
      });
    } else {
      setApiKeys({ openai: '', anthropic: '' });
      setApiKeyStatus({
        openai: { valid: false, available: false },
        anthropic: { valid: false, available: false }
      });
    }
  }, [user]);

  // Load available models whenever API keys change
  useEffect(() => {
    const loadAvailableModels = async () => {
      setLoading(true);
      try {
        const validApiKeys = {};
        
        // Only include valid API keys
        if (apiKeyStatus.openai.valid && apiKeys.openai) {
          validApiKeys.openai = apiKeys.openai;
        }
        if (apiKeyStatus.anthropic.valid && apiKeys.anthropic) {
          validApiKeys.anthropic = apiKeys.anthropic;
        }

        const models = await getAvailableModelsWithApiKeys(validApiKeys);
        setAvailableModels(models);
      } catch (error) {
        console.error('Error loading available models:', error);
        // Fallback to default models
        setAvailableModels([
              {
      id: 'llama-3.1-8b-instant',
      name: 'Llama 3 8B',
      provider: 'Groq',
      description: 'Fast, efficient model',
      type: 'internal'
    },
    {
      id: 'llama-3.3-70b-versatile',
      name: 'Llama 3 70B',
      provider: 'Groq',
      description: 'High accuracy model',
      type: 'internal'
    },
    {
      id: 'gemma2-9b-it',
      name: 'Gemma 2',
      provider: 'Groq',
      description: 'Instruction-tuned model',
      type: 'internal'
    }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadAvailableModels();
  }, [apiKeys, apiKeyStatus]);

  // Get valid API keys for requests
  const getValidApiKeys = () => {
    const validKeys = {};
    if (apiKeyStatus.openai.valid && apiKeys.openai) {
      validKeys.openai = apiKeys.openai;
    }
    if (apiKeyStatus.anthropic.valid && apiKeys.anthropic) {
      validKeys.anthropic = apiKeys.anthropic;
    }
    return validKeys;
  };

  // Check if a specific provider is available
  const isProviderAvailable = (provider) => {
    return apiKeyStatus[provider]?.valid && apiKeyStatus[provider]?.available;
  };

  // Get models for a specific provider
  const getModelsByProvider = (provider) => {
    return availableModels.filter(model => 
      model.provider.toLowerCase() === provider.toLowerCase() || 
      model.type === provider
    );
  };

  // Get all external models (OpenAI + Anthropic)
  const getExternalModels = () => {
    return availableModels.filter(model => model.type === 'external');
  };

  // Get all internal models
  const getInternalModels = () => {
    return availableModels.filter(model => model.type === 'internal');
  };

  // Update API keys (called from UserSettings)
  const updateApiKeys = (newApiKeys, newStatus) => {
    setApiKeys(newApiKeys);
    setApiKeyStatus(newStatus);
  };

  // Check if any external API is available
  const hasExternalApis = () => {
    return isProviderAvailable('openai') || isProviderAvailable('anthropic');
  };

  const value = {
    apiKeys,
    apiKeyStatus,
    availableModels,
    loading,
    getValidApiKeys,
    isProviderAvailable,
    getModelsByProvider,
    getExternalModels,
    getInternalModels,
    updateApiKeys,
    hasExternalApis
  };

  return (
    <ApiKeysContext.Provider value={value}>
      {children}
    </ApiKeysContext.Provider>
  );
};

export default ApiKeysContext; 